import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { clientsApi } from "@/api";
import Header from "@/components/Header";

const ClientsScreen = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll();
      setClients(response.data);
    } catch (error) {
      // Silently handle errors in production
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients, location.key]);

  const filteredClients = clients.filter(
    (client) =>
      client.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20 md:pt-16">
      <Header title={t('clients.title')} />
      
      <div className="max-w-lg mx-auto p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-4 flex items-center px-4">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={t('clients.searchPlaceholder')}
            className="flex-1 p-3 outline-none bg-transparent dark:text-white"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-6xl mb-4">👥</div>
            <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
              {searchQuery ? t('common.noResults') : t('clients.noClients')}
            </p>
            <p className="text-gray-400 text-sm mb-4">
              {searchQuery ? '' : t('clients.addFirstClient')}
            </p>
            {!searchQuery && (
              <button 
                onClick={() => navigate('/nuovo-cliente')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-indigo-700"
              >
                + {t('clients.addClient')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                onClick={() => navigate(`/cliente/${client.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex items-center cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`client-card-${client.id}`}
              >
                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  {client.first_name[0]}{client.last_name[0]}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{client.first_name} {client.last_name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{client.email}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-indigo-600">💪</span>
                    <span className="text-xs text-indigo-600 dark:text-indigo-400">{client.remaining_lessons} {t('clients.remainingLessons').toLowerCase()}</span>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => navigate('/nuovo-cliente')}
          className="fixed bottom-24 right-4 md:bottom-4 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg flex items-center justify-center text-3xl hover:bg-indigo-700 transition-colors"
          data-testid="add-client-button"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default ClientsScreen;
